/**
 * DailyReport Component
 * 
 * A comprehensive daily construction report form used to document:
 * - Weather conditions
 * - Workforce/manpower
 * - Work performed
 * - Equipment used
 * - Materials received
 * - Safety observations
 * - Delays and issues
 * 
 * Created: December 2024
 */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useResponsive } from '../../hooks/useResponsive';

const DailyReport = () => {
  const { projectId } = useParams();
  const { isMobile } = useResponsive();
  const today = new Date().toISOString().split('T')[0];

  // Form state
  const [report, setReport] = useState({
    date: today,
    reportNumber: `DR-${Date.now().toString().slice(-6)}`,
    preparedBy: '',
    
    // Weather
    weatherMorning: 'Clear',
    weatherAfternoon: 'Clear',
    tempHigh: '',
    tempLow: '',
    precipitation: 'None',
    
    // Work Hours
    startTime: '07:00',
    endTime: '17:00',
    
    // Manpower
    manpower: [
      { trade: 'Pipefitters', count: 0, hours: 0 },
      { trade: 'Welders', count: 0, hours: 0 },
      { trade: 'Helpers', count: 0, hours: 0 },
      { trade: 'Supervisors', count: 0, hours: 0 },
      { trade: 'Safety', count: 0, hours: 0 },
    ],
    
    // Work Performed
    workPerformed: '',
    
    // Equipment
    equipment: [
      { name: '', hours: 0, status: 'Operating' },
    ],
    
    // Materials
    materialsReceived: '',
    materialsUsed: '',
    
    // Safety
    safetyMeeting: true,
    safetyTopic: '',
    incidents: 'None',
    nearMisses: '',
    
    // Issues
    delays: '',
    delayHours: 0,
    issues: '',
    
    // Visitors
    visitors: '',
    
    // Notes
    notes: '',
  });

  const [saved, setSaved] = useState(false);

  // Update form field
  const updateField = (field, value) => {
    setReport(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  // Update manpower
  const updateManpower = (index, field, value) => {
    const newManpower = [...report.manpower];
    newManpower[index][field] = field === 'trade' ? value : parseInt(value) || 0;
    setReport(prev => ({ ...prev, manpower: newManpower }));
    setSaved(false);
  };

  // Add manpower row
  const addManpowerRow = () => {
    setReport(prev => ({
      ...prev,
      manpower: [...prev.manpower, { trade: '', count: 0, hours: 0 }]
    }));
  };

  // Update equipment
  const updateEquipment = (index, field, value) => {
    const newEquipment = [...report.equipment];
    newEquipment[index][field] = field === 'hours' ? (parseInt(value) || 0) : value;
    setReport(prev => ({ ...prev, equipment: newEquipment }));
    setSaved(false);
  };

  // Add equipment row
  const addEquipmentRow = () => {
    setReport(prev => ({
      ...prev,
      equipment: [...prev.equipment, { name: '', hours: 0, status: 'Operating' }]
    }));
  };

  // Calculate totals
  const totalWorkers = report.manpower.reduce((sum, m) => sum + m.count, 0);
  const totalManHours = report.manpower.reduce((sum, m) => sum + (m.count * m.hours), 0);

  // Handle save
  const handleSave = () => {
    console.log('Saving report:', report);
    setSaved(true);
    // TODO: API call to save report
    alert('Report saved successfully!');
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Styles
  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: isMobile ? '16px' : '24px',
    marginBottom: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    border: '1px solid #e9ecef',
  };

  const sectionTitle = {
    margin: '0 0 16px 0',
    color: '#333',
    fontSize: '1.1rem',
    fontWeight: '600',
    borderBottom: '2px solid #dc3545',
    paddingBottom: '8px',
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '500',
    color: '#555',
    fontSize: '14px',
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  };

  const buttonStyle = {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    borderRadius: '8px',
    cursor: 'pointer',
    border: 'none',
    transition: 'background-color 0.2s',
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }} className="daily-report">
      {/* Header */}
      <div style={{ 
        ...cardStyle, 
        background: 'linear-gradient(135deg, #1a1a1a 0%, #dc3545 100%)',
        color: 'white',
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '16px',
        }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '1.5rem' : '2rem' }}>
              📋 Daily Construction Report
            </h1>
            <p style={{ margin: 0, opacity: 0.9 }}>
              Report #{report.reportNumber} | Project ID: {projectId}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              onClick={handlePrint}
              style={{ ...buttonStyle, backgroundColor: 'white', color: '#333' }}
            >
              🖨️ Print
            </button>
            <button 
              onClick={handleSave}
              style={{ ...buttonStyle, backgroundColor: '#28a745', color: 'white' }}
            >
              💾 Save
            </button>
          </div>
        </div>
      </div>

      {/* Basic Info */}
      <div style={cardStyle}>
        <h3 style={sectionTitle}>📅 Report Information</h3>
        <div style={gridStyle}>
          <div>
            <label style={labelStyle}>Date *</label>
            <input
              type="date"
              value={report.date}
              onChange={(e) => updateField('date', e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Report Number</label>
            <input
              type="text"
              value={report.reportNumber}
              onChange={(e) => updateField('reportNumber', e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Prepared By *</label>
            <input
              type="text"
              value={report.preparedBy}
              onChange={(e) => updateField('preparedBy', e.target.value)}
              placeholder="Your name"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Work Hours</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="time"
                value={report.startTime}
                onChange={(e) => updateField('startTime', e.target.value)}
                style={{ ...inputStyle, width: 'auto' }}
              />
              <span>to</span>
              <input
                type="time"
                value={report.endTime}
                onChange={(e) => updateField('endTime', e.target.value)}
                style={{ ...inputStyle, width: 'auto' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Weather */}
      <div style={cardStyle}>
        <h3 style={sectionTitle}>🌤️ Weather Conditions</h3>
        <div style={gridStyle}>
          <div>
            <label style={labelStyle}>Morning</label>
            <select
              value={report.weatherMorning}
              onChange={(e) => updateField('weatherMorning', e.target.value)}
              style={inputStyle}
            >
              <option>Clear</option>
              <option>Partly Cloudy</option>
              <option>Cloudy</option>
              <option>Rain</option>
              <option>Snow</option>
              <option>Fog</option>
              <option>Windy</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Afternoon</label>
            <select
              value={report.weatherAfternoon}
              onChange={(e) => updateField('weatherAfternoon', e.target.value)}
              style={inputStyle}
            >
              <option>Clear</option>
              <option>Partly Cloudy</option>
              <option>Cloudy</option>
              <option>Rain</option>
              <option>Snow</option>
              <option>Fog</option>
              <option>Windy</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>High Temp (°C)</label>
            <input
              type="number"
              value={report.tempHigh}
              onChange={(e) => updateField('tempHigh', e.target.value)}
              placeholder="e.g., 25"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Low Temp (°C)</label>
            <input
              type="number"
              value={report.tempLow}
              onChange={(e) => updateField('tempLow', e.target.value)}
              placeholder="e.g., 15"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Precipitation</label>
            <select
              value={report.precipitation}
              onChange={(e) => updateField('precipitation', e.target.value)}
              style={inputStyle}
            >
              <option>None</option>
              <option>Light Rain</option>
              <option>Heavy Rain</option>
              <option>Snow</option>
              <option>Sleet</option>
            </select>
          </div>
        </div>
      </div>

      {/* Manpower */}
      <div style={cardStyle}>
        <h3 style={sectionTitle}>👷 Manpower</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Trade/Craft</th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '100px' }}>Workers</th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '100px' }}>Hours Each</th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '100px' }}>Total Hours</th>
            </tr>
          </thead>
          <tbody>
            {report.manpower.map((row, idx) => (
              <tr key={idx}>
                <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>
                  <input
                    type="text"
                    value={row.trade}
                    onChange={(e) => updateManpower(idx, 'trade', e.target.value)}
                    style={{ ...inputStyle, border: 'none', backgroundColor: 'transparent' }}
                    placeholder="Enter trade"
                  />
                </td>
                <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                  <input
                    type="number"
                    value={row.count || ''}
                    onChange={(e) => updateManpower(idx, 'count', e.target.value)}
                    style={{ ...inputStyle, width: '80px', textAlign: 'center' }}
                    min="0"
                  />
                </td>
                <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                  <input
                    type="number"
                    value={row.hours || ''}
                    onChange={(e) => updateManpower(idx, 'hours', e.target.value)}
                    style={{ ...inputStyle, width: '80px', textAlign: 'center' }}
                    min="0"
                    max="24"
                  />
                </td>
                <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'center', fontWeight: '600' }}>
                  {row.count * row.hours}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: '#f8f9fa', fontWeight: '600' }}>
              <td style={{ padding: '12px' }}>TOTAL</td>
              <td style={{ padding: '12px', textAlign: 'center' }}>{totalWorkers}</td>
              <td style={{ padding: '12px', textAlign: 'center' }}>-</td>
              <td style={{ padding: '12px', textAlign: 'center', color: '#dc3545' }}>{totalManHours}</td>
            </tr>
          </tfoot>
        </table>
        <button
          onClick={addManpowerRow}
          style={{ 
            marginTop: '12px', 
            padding: '8px 16px', 
            backgroundColor: '#f8f9fa', 
            border: '1px dashed #ccc',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          + Add Trade
        </button>
      </div>

      {/* Work Performed */}
      <div style={cardStyle}>
        <h3 style={sectionTitle}>🔧 Work Performed Today</h3>
        <textarea
          value={report.workPerformed}
          onChange={(e) => updateField('workPerformed', e.target.value)}
          placeholder="Describe work activities completed today..."
          style={{ ...inputStyle, minHeight: '150px', resize: 'vertical' }}
        />
      </div>

      {/* Equipment */}
      <div style={cardStyle}>
        <h3 style={sectionTitle}>🚜 Equipment Used</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Equipment</th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '100px' }}>Hours</th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '150px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {report.equipment.map((row, idx) => (
              <tr key={idx}>
                <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>
                  <input
                    type="text"
                    value={row.name}
                    onChange={(e) => updateEquipment(idx, 'name', e.target.value)}
                    style={{ ...inputStyle, border: 'none', backgroundColor: 'transparent' }}
                    placeholder="Equipment name"
                  />
                </td>
                <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                  <input
                    type="number"
                    value={row.hours || ''}
                    onChange={(e) => updateEquipment(idx, 'hours', e.target.value)}
                    style={{ ...inputStyle, width: '80px', textAlign: 'center' }}
                    min="0"
                  />
                </td>
                <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                  <select
                    value={row.status}
                    onChange={(e) => updateEquipment(idx, 'status', e.target.value)}
                    style={{ ...inputStyle, width: 'auto' }}
                  >
                    <option>Operating</option>
                    <option>Idle</option>
                    <option>Maintenance</option>
                    <option>Breakdown</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          onClick={addEquipmentRow}
          style={{ 
            marginTop: '12px', 
            padding: '8px 16px', 
            backgroundColor: '#f8f9fa', 
            border: '1px dashed #ccc',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          + Add Equipment
        </button>
      </div>

      {/* Materials */}
      <div style={cardStyle}>
        <h3 style={sectionTitle}>📦 Materials</h3>
        <div style={gridStyle}>
          <div>
            <label style={labelStyle}>Materials Received</label>
            <textarea
              value={report.materialsReceived}
              onChange={(e) => updateField('materialsReceived', e.target.value)}
              placeholder="List materials received on site today..."
              style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
            />
          </div>
          <div>
            <label style={labelStyle}>Materials Used/Installed</label>
            <textarea
              value={report.materialsUsed}
              onChange={(e) => updateField('materialsUsed', e.target.value)}
              placeholder="List materials used or installed today..."
              style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
            />
          </div>
        </div>
      </div>

      {/* Safety */}
      <div style={cardStyle}>
        <h3 style={sectionTitle}>🦺 Safety</h3>
        <div style={gridStyle}>
          <div>
            <label style={labelStyle}>Safety Meeting Held?</label>
            <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={report.safetyMeeting === true}
                  onChange={() => updateField('safetyMeeting', true)}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={report.safetyMeeting === false}
                  onChange={() => updateField('safetyMeeting', false)}
                />
                No
              </label>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Safety Topic Discussed</label>
            <input
              type="text"
              value={report.safetyTopic}
              onChange={(e) => updateField('safetyTopic', e.target.value)}
              placeholder="e.g., Fall Protection"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Incidents</label>
            <select
              value={report.incidents}
              onChange={(e) => updateField('incidents', e.target.value)}
              style={inputStyle}
            >
              <option>None</option>
              <option>Near Miss</option>
              <option>First Aid</option>
              <option>Medical Treatment</option>
              <option>Lost Time</option>
              <option>Fatality</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Near Misses / Safety Observations</label>
            <textarea
              value={report.nearMisses}
              onChange={(e) => updateField('nearMisses', e.target.value)}
              placeholder="Describe any near misses or safety observations..."
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>
        </div>
      </div>

      {/* Delays & Issues */}
      <div style={cardStyle}>
        <h3 style={sectionTitle}>⚠️ Delays & Issues</h3>
        <div style={gridStyle}>
          <div>
            <label style={labelStyle}>Delays</label>
            <textarea
              value={report.delays}
              onChange={(e) => updateField('delays', e.target.value)}
              placeholder="Describe any delays encountered..."
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>
          <div>
            <label style={labelStyle}>Delay Hours</label>
            <input
              type="number"
              value={report.delayHours || ''}
              onChange={(e) => updateField('delayHours', parseInt(e.target.value) || 0)}
              placeholder="0"
              style={inputStyle}
              min="0"
            />
          </div>
          <div style={{ gridColumn: isMobile ? 'auto' : '1 / -1' }}>
            <label style={labelStyle}>Issues / Problems</label>
            <textarea
              value={report.issues}
              onChange={(e) => updateField('issues', e.target.value)}
              placeholder="Describe any issues or problems encountered..."
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>
        </div>
      </div>

      {/* Visitors & Notes */}
      <div style={cardStyle}>
        <h3 style={sectionTitle}>📝 Additional Information</h3>
        <div style={gridStyle}>
          <div>
            <label style={labelStyle}>Site Visitors</label>
            <textarea
              value={report.visitors}
              onChange={(e) => updateField('visitors', e.target.value)}
              placeholder="List any visitors to the site today..."
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>
          <div>
            <label style={labelStyle}>Additional Notes</label>
            <textarea
              value={report.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder="Any other notes or comments..."
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div style={{ 
        ...cardStyle, 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        <div style={{ color: '#666', fontSize: '14px' }}>
          {saved ? '✅ Report saved' : '⚠️ Unsaved changes'}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            onClick={handlePrint}
            style={{ ...buttonStyle, backgroundColor: '#6c757d', color: 'white' }}
          >
            🖨️ Print Report
          </button>
          <button 
            onClick={handleSave}
            style={{ ...buttonStyle, backgroundColor: '#dc3545', color: 'white' }}
          >
            💾 Save Report
          </button>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .daily-report { max-width: 100% !important; }
          button { display: none !important; }
          input, textarea, select { 
            border: none !important; 
            background: none !important;
            padding: 4px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DailyReport;

